import { Record, Map } from 'immutable';
import type { AnyAction } from 'redux';
import { defaultMemoize } from 'reselect';
import { LOCATION_CHANGE, SAVE_LOCATION_DATA } from '@/domain/router/redux/reduxActions';

import { parseQuery } from '@/domain/router/helpers';
import * as actions from '@/domain/router/RouterActions';
import * as envActions from '@/domain/env/envActions';

import type { LocationShape, Query, RoutingShape, RoutingState } from '@/domain/router/types';

const LocationFactory = Record<LocationShape>({
  pathname: '',
  search: '',
  hash: '',
  key: '',
  state: Map(),
  query: {},
});

export const RouterFactory = Record<RoutingShape>({
  location: LocationFactory(),
  prevLocation: LocationFactory(),
  companyId: null,
  // This must be null by default. When accessing link directly (in a new tab with no history) router will pass empty string
  // and in case default value being empty string App component will not recognize complete pros change and will not force rerender
  // while when navigating through app routes will cause props to always change. This leads to inconsistency between direct link
  // and navigationg through app behaviours, making direct link scenarion have less rerenders.
  complete: null,
  customSingOut: false,
});

const queryAdapter = defaultMemoize((search: string): Query => parseQuery(search));

export const reducer = {
  routing(state: RoutingState = RouterFactory(), action: AnyAction): RoutingState {
    switch (action.type) {
      case LOCATION_CHANGE: {
        const {
          payload: { location, companyId },
        } = action;

        return state
          .set(
            'location',
            LocationFactory({
              ...location,
              query: queryAdapter(location.search),
              state: Map({ ...location.state }),
            }),
          )
          .set('prevLocation', state.location)
          .set('companyId', companyId);
      }
      case SAVE_LOCATION_DATA: {
        const data = action.payload;

        return state.setIn(['location', 'state'], Map({ ...state.location.state.toJS(), ...data }));
      }
      case actions.setRouteCompanyIdAction.type:
        return state.set('companyId', action.payload);

      case actions.completeEmptyAction.type:
      case actions.completeResetAction.type:
      case actions.completeAction.type:
        return state.set('complete', action.payload);

      case envActions.signOutAction.type:
        return state.set('customSingOut', !!action.payload);
      default:
        return state;
    }
  },
};
